/**
 * Client-side annotation store for the viewer-data block lab.
 *
 * Annotations are a DESIGN PROTOTYPE — they live in localStorage keyed by
 * block id (no backend), so they survive reloads but never leave the device.
 * Four kinds are supported:
 *   - `text`  — a written comment
 *   - `voice` — a recorded memo (base64 audio, short clips only)
 *   - `image` — an attached picture (base64)
 *   - `link`  — a pointer to another block in the same collection
 *
 * Any annotation may optionally reference a part of the block it annotates:
 *   - on an image block, a normalized rectangle (`region`);
 *   - on a text block, a quoted substring (`quote`).
 */
import { useCallback, useEffect, useState } from "react";
const KEY_PREFIX = "vlab:annotations:";
function keyFor(blockId) {
    return `${KEY_PREFIX}${blockId}`;
}
export function loadAnnotations(blockId) {
    try {
        const raw = localStorage.getItem(keyFor(blockId));
        if (!raw)
            return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    }
    catch {
        return [];
    }
}
const annotationsListeners = new Map();
// A second, block-agnostic channel for consumers that watch *every* block's
// annotations at once (the Latest Activity feed). Fired on any save so the feed
// reflects a new comment immediately, wherever it was made.
const allAnnotationsListeners = new Set();
function subscribeAnnotations(blockId, fn) {
    let set = annotationsListeners.get(blockId);
    if (!set) {
        set = new Set();
        annotationsListeners.set(blockId, set);
    }
    set.add(fn);
    return () => {
        set.delete(fn);
        if (set.size === 0)
            annotationsListeners.delete(blockId);
    };
}
function notifyAnnotations(blockId) {
    const perBlock = annotationsListeners.get(blockId);
    const fns = [...(perBlock ?? []), ...allAnnotationsListeners];
    if (fns.length === 0)
        return;
    // Defer out of the current render/commit: `saveAnnotations` runs inside a
    // state updater, so calling subscribers synchronously would setState on
    // other components mid-render.
    queueMicrotask(() => {
        for (const fn of fns)
            fn();
    });
}
function saveAnnotations(blockId, annotations) {
    try {
        localStorage.setItem(keyFor(blockId), JSON.stringify(annotations));
    }
    catch {
        // Quota exceeded / storage disabled — prototype, so silently drop.
    }
    notifyAnnotations(blockId);
}
function newAnnotationId() {
    try {
        return crypto.randomUUID();
    }
    catch {
        return `a-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
    }
}
/**
 * Append one annotation to a block's list (id/createdAt assigned here) and
 * persist it. Standalone — for callers outside React (e.g. the quick-capture
 * box attaching a comment to a freshly-captured block by its client id).
 * Notifies every annotation subscriber, so any open surface reflects it.
 */
export function addAnnotation(blockId, annotation) {
    const created = { ...annotation, id: newAnnotationId(), createdAt: Date.now() };
    saveAnnotations(blockId, [...loadAnnotations(blockId), created]);
    return created;
}
/**
 * Read/write the annotations for one block, mirrored to localStorage. Reloads
 * when `blockId` changes; mutations are immediately persisted.
 */
export function useBlockAnnotations(blockId) {
    const [annotations, setAnnotations] = useState([]);
    useEffect(() => {
        setAnnotations(blockId ? loadAnnotations(blockId) : []);
    }, [blockId]);
    const mutate = useCallback((fn) => {
        setAnnotations((prev) => {
            const next = fn(prev);
            if (blockId)
                saveAnnotations(blockId, next);
            return next;
        });
    }, [blockId]);
    const add = useCallback((a) => {
        mutate((prev) => [...prev, { ...a, id: newAnnotationId(), createdAt: Date.now() }]);
    }, [mutate]);
    const remove = useCallback((id) => {
        mutate((prev) => prev.filter((x) => x.id !== id));
    }, [mutate]);
    return { annotations, add, remove };
}
/**
 * Read-only, reactive view of a block's annotations for lightweight surfaces
 * (grid tiles showing a comment count / region overlay). Stays in sync with
 * edits made anywhere in the tab — the editing hook writes through
 * `saveAnnotations`, which notifies subscribers.
 */
export function useBlockAnnotationsRead(blockId) {
    const [annotations, setAnnotations] = useState(() => loadAnnotations(blockId));
    useEffect(() => {
        setAnnotations(loadAnnotations(blockId));
        return subscribeAnnotations(blockId, () => setAnnotations(loadAnnotations(blockId)));
    }, [blockId]);
    return annotations;
}
/**
 * Every annotation across every block, flattened. Walks the localStorage keys
 * under our prefix — there's no backend index, so this is how the feed learns
 * what comments exist. Order is unspecified; callers sort by `createdAt`.
 */
export function loadAllAnnotations() {
    const out = [];
    try {
        for (let i = 0; i < localStorage.length; i += 1) {
            const key = localStorage.key(i);
            if (!key || !key.startsWith(KEY_PREFIX))
                continue;
            const blockId = key.slice(KEY_PREFIX.length);
            for (const annotation of loadAnnotations(blockId))
                out.push({ blockId, annotation });
        }
    }
    catch {
        // Storage disabled — prototype, so just yield nothing.
    }
    return out;
}
function subscribeAllAnnotations(fn) {
    allAnnotationsListeners.add(fn);
    return () => {
        allAnnotationsListeners.delete(fn);
    };
}
/**
 * Reactive view of every annotation in the tab, re-read whenever any comment is
 * added or removed anywhere. Powers the Latest Activity feed.
 */
export function useAllAnnotations() {
    const [items, setItems] = useState(loadAllAnnotations);
    useEffect(() => {
        const read = () => setItems(loadAllAnnotations());
        read();
        return subscribeAllAnnotations(read);
    }, []);
    return items;
}
/** "0:07", "1:23" from a millisecond duration. */
export function formatDuration(ms) {
    const total = Math.round((ms ?? 0) / 1000);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
}
// ---------------------------------------------------------------------------
// Global "show annotations" preference (debug toggle)
// ---------------------------------------------------------------------------
//
// The annotations layer is shown by DEFAULT. The debug panel exposes a single
// "Hide annotations sidebar" toggle to suppress it — when hidden, the viewer is
// clean: no annotation panel, no tile comment badges, no region overlays, no
// comment column. The flag lives in localStorage so it survives reloads, with
// its own in-tab subscription channel so the debug panel and every annotation
// surface stay in sync.
const SHOW_KEY = "vlab:annotations-shown";
function loadShowAnnotations() {
    try {
        // Shown by default — only an explicit "0" (set via the debug "Hide
        // annotations sidebar" toggle) suppresses the layer.
        return localStorage.getItem(SHOW_KEY) !== "0";
    }
    catch {
        return true;
    }
}
const showListeners = new Set();
function subscribeShowAnnotations(fn) {
    showListeners.add(fn);
    return () => {
        showListeners.delete(fn);
    };
}
function saveShowAnnotations(on) {
    try {
        localStorage.setItem(SHOW_KEY, on ? "1" : "0");
    }
    catch {
        // Storage disabled — prototype, so silently drop.
    }
    // Defer out of the current render/commit, mirroring `notifyAnnotations`.
    const fns = [...showListeners];
    queueMicrotask(() => {
        for (const fn of fns)
            fn();
    });
}
/** Reactive "show the annotations prototype" debug preference (default on). */
export function useShowAnnotations() {
    const [value, setValue] = useState(loadShowAnnotations);
    useEffect(() => {
        const read = () => setValue(loadShowAnnotations());
        read();
        return subscribeShowAnnotations(read);
    }, []);
    const set = useCallback((on) => saveShowAnnotations(on), []);
    return [value, set];
}
